import { defineNuxtPlugin, reloadNuxtApp } from '#app';

/**
 * Chunk-load failure recovery: reloads the app when a dynamic import fails
 * (`app:chunkError`).
 *
 * The reload is COUNTED and capped. Without a cap, a persistent cause —
 * typically edge rate limiting — makes the error resurface right after the
 * reload and trigger another one. Measured in production (2026-08-05): 4
 * sessions accumulated 2260 page loads with ZERO navigations, over 1 to 4
 * hours each. The cycle feeds itself because every reload re-emits ~130
 * `modulepreload` hints, which trips the limiter again. Besides wrecking the
 * user's session, it accounted for ~90% of the events feeding the JS error
 * monitor.
 *
 * The window tells a loop apart from a recurrence: two failures seconds apart
 * are the loop; two visits on the same day are not.
 *
 * Once the cap is reached the hook does nothing and the error takes its normal
 * course to `app/error.vue` — a screen with an action, instead of a page
 * flickering endlessly.
 */
const MAX_RELOAD_ATTEMPTS = 2;
const WINDOW_MS = 30_000;
const STORAGE_KEY = 'chunk-error:attempts';

interface ReloadAttempt {
	/** Reload attempts already made within the window. */
	count: number;
	/** Timestamp of the last attempt, in epoch ms. */
	at: number;
}

/**
 * Attempts already made in the current window. Storage that is unavailable,
 * empty or corrupted counts as zero: worst case the behaviour falls back to
 * the previous one (always reload), never leaving the user without recovery.
 */
function readAttempts(now: number): number {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return 0;

		const previous = JSON.parse(raw) as ReloadAttempt;
		if (
			typeof previous?.count !== 'number' ||
			typeof previous?.at !== 'number'
		) {
			return 0;
		}

		// Outside the window = new incident, not a continuation of the loop.
		return now - previous.at < WINDOW_MS ? previous.count : 0;
	} catch {
		return 0;
	}
}

function recordAttempt(count: number, at: number): void {
	try {
		const attempt: ReloadAttempt = { count, at };
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt));
	} catch {
		// No storage means no way to count; proceed to the reload anyway.
	}
}

export default defineNuxtPlugin(nuxtApp => {
	nuxtApp.hook('app:chunkError', () => {
		const now = Date.now();
		const attempts = readAttempts(now);

		if (attempts >= MAX_RELOAD_ATTEMPTS) return;

		recordAttempt(attempts + 1, now);
		reloadNuxtApp({ persistState: true });
	});
});
